import {
  Box,
  Card,
  Title,
  Checkbox,
  Tooltip,
  Icon,
  Alert,
  Text,
} from '@nimbus-ds/components';
import { InfoCircleIcon } from '@nimbus-ds/icons';
import { PromotionTypes } from '@tiendanube/common/src/domains/marketing/promotions/PromotionTypes';
import { useTranslationMarketing } from 'domains/Marketing/hook';
import { PromotionFormProps } from '../PromotionForm';

const COMBINATIONS_INFO_THRESHOLD = 2;

type GranularDiscountCombinationProps = Pick<
  PromotionFormProps,
  'values' | 'onChange'
>;

export function GranularDiscountCombination({
  values: {
    promotionType,
    combinesWithQuantityDiscounts,
    combinesWithFreeShipping,
    combinesWithCartAmountDiscounts,
    combinesWithAppDiscounts,
    combinesWithPriceDiscounts,
  },
  onChange,
}: Readonly<GranularDiscountCombinationProps>) {
  const t = useTranslationMarketing(
    'promotions.settingsPage.applyTo.granularCombination',
  );

  const getAvailableCheckboxes = () => {
    const quantityDiscountsCheckbox = {
      name: 'combinesWithQuantityDiscounts',
      label: t('quantityDiscounts'),
      checked: combinesWithQuantityDiscounts,
      tooltip: t('quantityDiscountsTooltip'),
    };

    const priceDiscountsCheckbox = {
      name: 'combinesWithPriceDiscounts',
      label: t('priceDiscounts'),
      checked: combinesWithPriceDiscounts,
      tooltip: t('priceDiscountsTooltip'),
    };

    const freeShippingCheckbox = {
      name: 'combinesWithFreeShipping',
      label: t('freeShipping'),
      checked: combinesWithFreeShipping,
      tooltip: undefined,
    };

    const cartAmountDiscountsCheckbox = {
      name: 'combinesWithCartAmountDiscounts',
      label: t('cartAmountDiscounts'),
      checked: combinesWithCartAmountDiscounts,
      tooltip: t('cartAmountDiscountsTooltip'),
    };

    const appDiscountsCheckbox = {
      name: 'combinesWithAppDiscounts',
      label: t('appDiscounts'),
      checked: combinesWithAppDiscounts,
      tooltip: undefined,
    };

    switch (promotionType) {
      case PromotionTypes.PERCENTAGE_OFF:
      case PromotionTypes.CROSS_SELLING:
        return [
          quantityDiscountsCheckbox,
          freeShippingCheckbox,
          cartAmountDiscountsCheckbox,
          appDiscountsCheckbox,
        ];
      case PromotionTypes.BUY_X_PAY_Y:
      case PromotionTypes.PROGRESSIVE_DISCOUNT:
        return [
          priceDiscountsCheckbox,
          freeShippingCheckbox,
          cartAmountDiscountsCheckbox,
          appDiscountsCheckbox,
        ];
      default:
        return [];
    }
  };

  const availableCheckboxes = getAvailableCheckboxes();

  if (availableCheckboxes.length === 0) {
    return null;
  }

  const selectedCount = availableCheckboxes.filter(
    (checkbox) => checkbox.checked,
  ).length;

  const showBanner = selectedCount >= COMBINATIONS_INFO_THRESHOLD;

  return (
    <Card>
      <Card.Header>
        <Box alignItems="center" display="flex" justifyContent="space-between">
          <Title as="h4">{t('title')}</Title>
        </Box>
      </Card.Header>
      <Card.Body>
        <Box display="flex" flexDirection="column" gap="3">
          {availableCheckboxes.map((checkbox) => (
            <Box key={checkbox.name} display="flex" alignItems="center" gap="2">
              <Checkbox
                name={checkbox.name}
                label={checkbox.label}
                onChange={onChange}
                checked={checkbox.checked}
              />
              {!!checkbox.tooltip && (
                <Tooltip content={checkbox.tooltip} arrow position="right">
                  <Icon
                    source={<InfoCircleIcon />}
                    color="primary-interactive"
                  />
                </Tooltip>
              )}
            </Box>
          ))}

          {showBanner && (
            <Box marginTop="3" display="flex" flexDirection="column" gap="2">
              <Text color="neutral-textHigh">
                {t('combinationBanner.titleStart')}{' '}
                <Text as="span" fontWeight="bold" color="neutral-textHigh">
                  {t('combinationBanner.titleMiddle', { count: selectedCount })}
                </Text>{' '}
                {t('combinationBanner.titleEnd')}
              </Text>
              <Alert
                appearance={
                  selectedCount === COMBINATIONS_INFO_THRESHOLD
                    ? 'primary'
                    : 'warning'
                }
              >
                {t('combinationBanner.message')}
              </Alert>
            </Box>
          )}
        </Box>
      </Card.Body>
    </Card>
  );
}
